"use client";

import React, { useState } from "react";
import styles from "./Details.module.scss";
import * as Accordion from "@radix-ui/react-accordion";
import { Plus, ArrowRight } from "lucide-react";
import If from "@/components/common/If";

import Link from "next/link";
import RichText from "@/components/common/RichText";

/**
 *
 * Details
 *
 */
const Details = ({ items, isSub = false, ...props }) => {
  const [currentOpen, setCurrentOpen] = useState<string | null>(null);
  return (
    <section className={styles.details} {...props}>
      <Accordion.Root
        className="w-full"
        type="single"
        collapsible
        onValueChange={(value) => {
          setCurrentOpen(value);
        }}
      >
        {items.map((item, index) => {
          if (item.link) {
            return (
              <Link href={item.link} key={item.title} target={item.external ? "_blank" : "_self"}>
                <div className={`pt-4 pb-3 w-full block ${isSub ? "" : "border-b border-b-white"}`}>
                  <div className="flex justify-between items-center w-full">
                    <div className="flex gap-2 items-center">
                      <h2
                        className="text-[1.25rem] font-hero text-current"
                        dangerouslySetInnerHTML={{ __html: item.title }}
                      />
                      <If condition={item.icon}>
                        <span className="text-sm font-thin text-white text-left">
                          {item.icon}
                        </span>
                      </If>
                    </div>
                    <ArrowRight
                      className="transition-transform duration-300 hover:translate-x-1"
                      size={32}
                      color="white"
                      strokeWidth={0.5}
                    />
                  </div>
                  <If condition={item.detail}>
                    <span className="text-sm font-thin text-white text-left">
                      {item.detail}
                    </span>
                  </If>
                </div>
              </Link>
            );
          } else {
            return (
              <Accordion.Item
                key={item.title}
                value={item.title}
                className={`pt-4 pb-3 w-full block ${
                  isSub ? "" : "border-b border-b-white"
                }`}
              >
                <Accordion.Header>
                  <Accordion.Trigger
                    className={`stack gap-2 w-full pb-1 ${styles.trigger}`}
                  >
                    <div className="flex justify-between items-center w-full">
                      <div className="flex gap-2 items-center">
                        <h2
                          className="text-[1.25rem] font-hero text-current"
                          dangerouslySetInnerHTML={{ __html: item.title }}
                        />
                        <If condition={item.icon}>
                          <span className="text-sm font-thin text-white text-left">
                            {item.icon}
                          </span>
                        </If>
                      </div>
                      <Plus
                        className={`transition-all ease-in-out duration-300 ${
                          currentOpen === item.title ? "rotate-45" : ""
                        } relative block`}
                        size={32}
                        color="white"
                        strokeWidth={0.5}
                      />
                    </div>
                    <If condition={item.detail}>
                      <span className="text-sm font-thin text-white text-left">
                        {item.detail}
                      </span>
                    </If>
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content
                    className={`
                                overflow-hidden transition-all duration-300 ease-in-out
                                data-[state=open]:animate-slideDown
                                data-[state=closed]:animate-slideUp
                                ${styles.content}
                              `}
                  >
                    <div className="py-4">
                      {item.description || item.children}
                      {item.content?.json && (
                        <RichText json={item.content.json} />
                      )}
                    </div>
                  </Accordion.Content>
              </Accordion.Item>
            );
          }
        })}
      </Accordion.Root>
    </section>
  );
};

export default Details;
